/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Event completion transitions
  EVENT_COMPLETED: 'transition/event-completed',
  CONFIRM_SERVICE: 'transition/confirm-service',
  CUSTOMER_MARK_NO_SHOW: 'transition/customer-mark-no-show',

  // Provider no-show evidence transitions
  PROVIDER_NO_SHOW_EVIDENCE: 'transition/provider-no-show-evidence',
  AUTO_NO_EVIDENCE_RECEIVED: 'transition/auto-no-evidence-received',

  // Operator or Admin confirms evidence or rejects evidence
  ADMIN_CONFIRMS_EVIDENCE: 'transition/admin-confirms-evidence',
  ADMIN_REJECTS_EVIDENCE: 'transition/admin-rejects-evidence',

  // Auto close transitions
  AUTO_CLOSE_ADJUSTMENT_WINDOW: 'transition/auto-close-adjustment-window',

  // Operator complete service transition
  OPERATOR_COMPLETE_SERVICE: 'transition/operator-complete-service',

  // transition synced
  TRANSACTION_SYNCED: 'transition/transaction-synced',

  // Transaction Auto Confirm
  AUTO_CONFIRM_SERVICE: 'transition/auto-confirm-service',

  // Auto release payment transition
  AUTO_RELEASE_PAYMENT: 'transition/auto-release-payment',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EVENT_COMPLETED: 'event-completed',
  SERVICE_CONFIRMED: 'service-confirmed',
  PROVIDER_NO_SHOW: 'provider-no-show',
  EVIDENCE_PROVIDED: 'evidence-provided',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  SYNCED: 'synced',
  PAYMENT_RELEASED: 'payment-released',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-payment/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.ACCEPTED]: {
      on: {
        [transitions.EVENT_COMPLETED]: states.EVENT_COMPLETED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.EXPIRE]: states.EXPIRED,
      },
    },
    [states.EVENT_COMPLETED]: {
      on: {
        [transitions.AUTO_CONFIRM_SERVICE]: states.SERVICE_CONFIRMED,
        [transitions.CONFIRM_SERVICE]: states.SERVICE_CONFIRMED,
        [transitions.CUSTOMER_MARK_NO_SHOW]: states.PROVIDER_NO_SHOW,
      },
    },
    [states.SERVICE_CONFIRMED]: {
      on: {
        [transitions.AUTO_CLOSE_ADJUSTMENT_WINDOW]: states.COMPLETED,
        [transitions.OPERATOR_COMPLETE_SERVICE]: states.COMPLETED,
      },
    },
    [states.PROVIDER_NO_SHOW]: {
      on: {
        [transitions.PROVIDER_NO_SHOW_EVIDENCE]: states.EVIDENCE_PROVIDED,
        [transitions.AUTO_NO_EVIDENCE_RECEIVED]: states.CANCELED,
      },
    },
    [states.EVIDENCE_PROVIDED]: {
      on: {
        [transitions.ADMIN_CONFIRMS_EVIDENCE]: states.SERVICE_CONFIRMED,
        [transitions.ADMIN_REJECTS_EVIDENCE]: states.CANCELED,
      },
    },
    [states.CANCELED]: {
      on: {
        [transitions.TRANSACTION_SYNCED]: states.SYNCED,
      }
    },
    [states.SYNCED]: {},
    [states.DELIVERED]: {},
    [states.COMPLETED]: {
      on: {
        [transitions.AUTO_RELEASE_PAYMENT]: states.PAYMENT_RELEASED,
      },
    },
    [states.PAYMENT_RELEASED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.PAYMENT_RELEASED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.EVENT_COMPLETED,
    transitions.CONFIRM_SERVICE,
    transitions.CUSTOMER_MARK_NO_SHOW,
    transitions.PROVIDER_NO_SHOW_EVIDENCE,
    transitions.ADMIN_CONFIRMS_EVIDENCE,
    transitions.ADMIN_REJECTS_EVIDENCE,
    transitions.AUTO_NO_EVIDENCE_RECEIVED,
    transitions.AUTO_CLOSE_ADJUSTMENT_WINDOW,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.AUTO_RELEASE_PAYMENT,
    transitions.CONFIRM_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.TRANSACTION_SYNCED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.TRANSACTION_SYNCED,
    transitions.OPERATOR_COMPLETE,
    transitions.AUTO_CLOSE_ADJUSTMENT_WINDOW,
    transitions.AUTO_RELEASE_PAYMENT,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.AUTO_NO_EVIDENCE_RECEIVED,
    transitions.ADMIN_REJECTS_EVIDENCE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
